import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';
import { formatOdds } from '../../utils/format';
import type { ParlayLeg } from '../../types';

// ── Odds helpers (mirror backend, no import cycle) ────────────────────────────

function americanToDecimal(american: number): number {
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}

function combinedDecimal(legs: ParlayLeg[]): number {
  return legs.reduce((prod, l) => prod * americanToDecimal(l.odds), 1);
}

function decimalToAmerican(decimal: number): number {
  if (decimal >= 2.0) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
}

function combinedModelProb(legs: ParlayLeg[]): number | null {
  const probs = legs.map((l) => l.modelProbability).filter((p): p is number => p != null);
  if (probs.length !== legs.length) return null;
  return probs.reduce((prod, p) => prod * p, 1);
}

// ── League badge ──────────────────────────────────────────────────────────────

const LEAGUE_COLORS: Record<string, string> = {
  mlb:  'bg-brand/20 text-brand-light',
  wnba: 'bg-purple-500/20 text-purple-300',
  nba:  'bg-orange-500/20 text-orange-300',
};

function LeagueBadge({ leagueId }: { leagueId: string }) {
  const cls = LEAGUE_COLORS[leagueId] ?? 'bg-surface-3 text-gray-400';
  return (
    <span className={clsx('text-[9px] font-bold uppercase px-1 py-0.5 rounded', cls)}>
      {leagueId}
    </span>
  );
}

// ── ParlaySlip ────────────────────────────────────────────────────────────────

export function ParlaySlip() {
  const {
    parlaySlip: legs,
    parlaySlipOpen: isOpen,
    setParlaySlipOpen,
    removeLegFromSlip,
    clearParlaySlip,
    placeParlay,
    bankrollSettings,
  } = useAppStore();

  const [stake, setStake]   = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError]    = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  if (!isOpen && legs.length === 0) return null;

  const decOdds     = legs.length >= 2 ? combinedDecimal(legs) : null;
  const combinedAmerican = decOdds ? decimalToAmerican(decOdds) : null;
  const impliedProb = decOdds ? 1 / decOdds : null;
  const modelProb   = legs.length >= 2 ? combinedModelProb(legs) : null;
  const edge        = modelProb != null && impliedProb != null ? modelProb - impliedProb : null;

  const stakeNum      = parseFloat(stake) || 0;
  const potentialReturn = decOdds && stakeNum > 0 ? stakeNum * decOdds : null;

  const unitSize = bankrollSettings?.unitSize ?? 10;

  async function handlePlace() {
    if (legs.length < 2) { setError('Add at least 2 legs to place a parlay.'); return; }
    if (stakeNum <= 0)   { setError('Enter a stake amount.'); return; }

    setPlacing(true);
    setError(null);
    try {
      await placeParlay({ legs, stakeAmount: stakeNum });
      setStake('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to place parlay.');
    } finally {
      setPlacing(false);
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="parlay-slip"
          initial={{ x: 320, opacity: 0 }}
          animate={{ x: 0,   opacity: 1 }}
          exit={{   x: 320, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed top-0 right-0 h-full w-80 z-50 flex flex-col bg-surface-1 border-l border-surface-4 shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-surface-4 bg-surface-0 flex-shrink-0">
            <Layers className="w-3.5 h-3.5 text-brand" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-300 flex-1">
              Parlay Slip
            </span>
            {legs.length > 0 && (
              <span className="text-[9px] font-bold bg-brand/20 text-brand-light rounded-full px-1.5 py-0.5">
                {legs.length}
              </span>
            )}
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors"
            >
              {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>
            <button
              onClick={() => setParlaySlipOpen(false)}
              className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          {!collapsed && (
            <>
              {/* Leg list */}
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                {legs.length === 0 && (
                  <p className="text-[11px] text-gray-500 text-center py-6">
                    Click <strong>+</strong> on any recommendation to add a leg.
                  </p>
                )}
                {legs.map((leg) => (
                  <div
                    key={leg.id}
                    className="rounded-md border border-surface-4 bg-surface-2 px-2.5 py-2"
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-0.5">
                          <LeagueBadge leagueId={leg.leagueId} />
                          <span className="text-[9px] text-gray-500 truncate">
                            {leg.awayTeam} @ {leg.homeTeam}
                          </span>
                        </div>
                        <p className="text-[11px] font-semibold text-gray-200 truncate">{leg.selection}</p>
                        <p className="text-[10px] text-gray-500">{leg.market}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={clsx(
                          'text-[11px] font-bold',
                          leg.odds > 0 ? 'text-win' : 'text-gray-300',
                        )}>
                          {formatOdds(leg.odds)}
                        </span>
                        <button
                          onClick={() => removeLegFromSlip(leg.id)}
                          className="text-gray-600 hover:text-loss transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Combined odds summary */}
              {legs.length >= 2 && combinedAmerican != null && (
                <div className="px-3 py-2 border-t border-surface-4 bg-surface-0/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-gray-500">Combined odds</span>
                    <span className="text-base font-bold text-brand-light">
                      {formatOdds(combinedAmerican)}
                    </span>
                  </div>
                  {impliedProb != null && (
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-500">Implied prob</span>
                      <span className="text-[11px] text-gray-400">
                        {(impliedProb * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {edge != null && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-500">Combined edge</span>
                      <span className={clsx(
                        'text-[11px] font-semibold',
                        edge > 0 ? 'text-win' : 'text-loss',
                      )}>
                        {edge > 0 ? '+' : ''}{(edge * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Stake + place */}
              <div className="px-3 py-3 border-t border-surface-4 space-y-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 w-12">Stake</span>
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-500">$</span>
                    <input
                      type="number"
                      min={0}
                      step={unitSize}
                      value={stake}
                      onChange={(e) => { setStake(e.target.value); setError(null); }}
                      placeholder={`${unitSize}`}
                      className="w-full bg-surface-2 border border-surface-4 rounded px-2 pl-5 py-1.5 text-[11px] text-gray-200 focus:outline-none focus:border-brand/60"
                    />
                  </div>
                </div>

                {potentialReturn != null && (
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-gray-500">To win</span>
                    <span className="text-win font-semibold">
                      ${(potentialReturn - stakeNum).toFixed(2)}
                    </span>
                  </div>
                )}

                {error && <p className="text-[10px] text-loss">{error}</p>}
                {success && <p className="text-[10px] text-win">Parlay placed!</p>}

                <div className="flex gap-2">
                  <button
                    onClick={clearParlaySlip}
                    className="flex-1 text-[11px] py-1.5 rounded border border-surface-4 text-gray-500 hover:text-gray-200 hover:border-surface-3 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handlePlace}
                    disabled={placing || legs.length < 2 || stakeNum <= 0}
                    className={clsx(
                      'flex-1 text-[11px] py-1.5 rounded font-semibold transition-colors',
                      legs.length >= 2 && stakeNum > 0 && !placing
                        ? 'bg-brand hover:bg-brand/80 text-white'
                        : 'bg-surface-3 text-gray-600 cursor-not-allowed',
                    )}
                  >
                    {placing ? 'Placing…' : 'Place Parlay'}
                  </button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
